import { create } from 'zustand'
import { toast } from 'react-toastify'
import fetcher from '../../../fetcher'

// Matches NotificationPagination.page_size on the backend.
export const PAGE_SIZE = 20

export const SOURCES = ['CRM', 'DEALS', 'LEADS', 'EMAIL', 'LISTINGS', 'SYSTEM']

// CREATED and DELIVERED both mean "the user hasn't looked at it". Delivery says
// the client received the row, which is not the same as a person seeing it —
// mirrors Notification.UNREAD_STATUSES on the backend, and the two must agree.
export const UNREAD_STATUSES = ['CREATED', 'DELIVERED']
export const isUnread = (n) => UNREAD_STATUSES.includes(n?.status)

const emptySummary = () => ({
    unread_count: 0,
    undelivered_count: 0,
    by_source: {},
    latest_at: null,
})

export const useNotificationsStore = create((set, get) => ({
    rows: [],
    page: 0,
    hasMore: true,
    loading: false,
    loadingMore: false,

    // Filters. `unreadOnly` and `source` both narrow the feed server-side —
    // never in JS, or the pagination would page over a set the server didn't
    // build and "load more" would skip rows.
    unreadOnly: false,
    source: null,
    // Archived is its own view, not another source filter — the default feed
    // excludes archived rows entirely (the backend's .inbox()), so asking for
    // them means sending a different parameter, not an extra one.
    archived: false,

    summary: emptySummary(),

    // Which row the detail pane is showing. Selection lives here rather than in
    // the page so the row, the pane and a future deep link all read one value.
    selectedId: null,
    select: (id) => set({ selectedId: id }),
    clearSelection: () => set({ selectedId: null }),

    setFilter: (patch) => {
        set(patch)
        // A filter change rebuilds the feed from page 1; keeping the old rows
        // would briefly show results the new filter excludes.
        get().fetchList({ reset: true })
    },

    buildParams: (page) => {
        const { unreadOnly, source, archived } = get()
        const params = new URLSearchParams({ page: String(page) })
        if (archived) {
            // status= and unread= are mutually exclusive server-side; archived
            // rows are by definition read, so unreadOnly can't also apply.
            params.set('status', 'ARCHIVED')
        } else if (unreadOnly) {
            params.set('unread', 'true')
        }
        if (source) params.set('source', source)
        return params.toString()
    },

    fetchList: async ({ reset = false } = {}) => {
        const { loading, loadingMore, page } = get()
        if (loading || loadingMore) return

        const nextPage = reset ? 1 : page + 1
        set(reset ? { loading: true } : { loadingMore: true })

        try {
            const { data } = await fetcher.getNotifications(get().buildParams(nextPage))
            set((state) => ({
                rows: reset ? data.results : [...state.rows, ...data.results],
                page: nextPage,
                hasMore: data.has_more,
                loading: false,
                loadingMore: false,
            }))
        } catch (e) {
            set({ loading: false, loadingMore: false })
            toast.error('Could not load notifications')
        }
    },

    fetchSummary: async () => {
        try {
            const { data } = await fetcher.getNotificationsSummary()
            set({ summary: data })
            return data
        } catch (e) {
            // Deliberately silent: this runs on a timer, and a transient failure
            // should not put a toast on screen every 30 seconds.
            return null
        }
    },

    // Optimistic — the row greys out the instant it's clicked, and a failure
    // puts it back. Waiting on the round trip makes the list feel broken.
    markRead: async (id) => {
        const before = get().rows
        set({
            rows: before.map((n) => (n.id === id ? { ...n, status: 'READ' } : n)),
            summary: { ...get().summary, unread_count: Math.max(0, get().summary.unread_count - 1) },
        })
        try {
            await fetcher.updateNotification(id, { status: 'READ' })
            get().fetchSummary()
        } catch (e) {
            set({ rows: before })
            toast.error('Could not mark that as read')
        }
    },

    archive: async (id) => {
        const before = get().rows
        // Archived rows leave the default feed entirely, so drop it rather than
        // restyling it — leaving it visible would misrepresent where it now is.
        set({ rows: before.filter((n) => n.id !== id) })
        try {
            await fetcher.updateNotification(id, { status: 'ARCHIVED' })
            get().fetchSummary()
        } catch (e) {
            set({ rows: before })
            toast.error('Could not archive that notification')
        }
    },

    // ARCHIVED -> READ. The backend restores to read rather than unread, so
    // pulling something back out of the archive can't re-alert you about it.
    unarchive: async (id) => {
        const before = get().rows
        // On the Archived view the row no longer belongs in the list; on any
        // other view it wasn't there to begin with. Dropping it is correct
        // either way, and a refetch would flash the stale row first.
        set({ rows: before.filter((n) => n.id !== id), selectedId: null })
        try {
            await fetcher.updateNotification(id, { status: 'READ' })
            get().fetchSummary()
        } catch (e) {
            set({ rows: before })
            toast.error('Could not unarchive that notification')
        }
    },

    markAllRead: async () => {
        const { source } = get()
        const before = get().rows
        set({ rows: before.map((n) => (isUnread(n) ? { ...n, status: 'READ' } : n)) })
        try {
            await fetcher.markNotificationsRead(source ? { source } : {})
            await get().fetchSummary()
            // Unread-only is now showing an empty set by definition; refetch so
            // the list matches the filter rather than displaying stale rows.
            if (get().unreadOnly) get().fetchList({ reset: true })
        } catch (e) {
            set({ rows: before })
            toast.error('Could not mark all as read')
        }
    },
}))
